<template>
  <div class="rrr-scrim" @click="$emit('close')">
    <div
      class="rrr-drawer"
      role="dialog"
      aria-modal="true"
      aria-label="Recall Reminder Text Report"
      @click.stop
    >
      <!-- Header -->
      <div class="rrr-head">
        <div class="rrr-headmeta">
          <h2 class="rrr-title">Recall Reminder Text Report</h2>
          <div class="rrr-sub">{{ subtitle }}</div>
        </div>
        <button
          type="button"
          id="rrr-close-btn"
          class="rrr-close"
          @click="$emit('close')"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
        <b-tooltip target="rrr-close-btn" triggers="hover" placement="left">Close</b-tooltip>
      </div>

      <!-- Body -->
      <div class="rrr-body">
        <!-- Filters -->
        <div class="rrr-filters">
          <div class="rrr-filter">
            <label class="rrr-label">Date From</label>
            <DatePicker
              lang="en"
              v-model="dateFrom"
              :format="dateFormatStr"
              input-class="form-control"
              class="w-100"
            >
              <i class="glyphicon glyphicon-th" slot="calendar-icon" />
            </DatePicker>
          </div>

          <div class="rrr-filter">
            <label class="rrr-label">Date To</label>
            <DatePicker
              lang="en"
              v-model="dateTo"
              :format="dateFormatStr"
              input-class="form-control"
              class="w-100"
            >
              <i class="glyphicon glyphicon-th" slot="calendar-icon" />
            </DatePicker>
          </div>

          <div class="rrr-filter">
            <label class="rrr-label">Channel</label>
            <b-form-select v-model="channel" :options="channelOptions" @change="onChannelChange" />
          </div>

          <div class="rrr-filter rrr-filter--actions">
            <b-button variant="primary" :disabled="loading" @click="load">
              <i class="fa fa-search mr-1" aria-hidden="true"></i> Apply
            </b-button>
            <b-button-group class="rrr-quick">
              <b-button
                size="sm"
                :variant="activeQuickRange === 30 ? 'primary' : 'outline-primary'"
                @click="setRange(30)"
              >
                Last 30 days
              </b-button>
              <b-button
                size="sm"
                :variant="activeQuickRange === 90 ? 'primary' : 'outline-primary'"
                @click="setRange(90)"
              >
                Last 90 days
              </b-button>
            </b-button-group>
          </div>
        </div>

        <b-alert :show="!!errorMsg" variant="danger" class="mb-3">
          <i class="fa fa-exclamation-triangle mr-1" aria-hidden="true"></i> {{ errorMsg }}
        </b-alert>

        <b-overlay :show="loading" rounded="sm">
          <!-- Summary tiles -->
          <div class="rrr-stat-cards">
            <div class="rrr-stat-card">
              <div class="rrr-stat-icon rrr-stat-icon--teal">
                <i class="fa fa-paper-plane" aria-hidden="true"></i>
              </div>
              <div>
                <div class="rrr-stat-num">{{ statValue(totalSent) }}</div>
                <div class="rrr-stat-label">{{ totalLabel }}</div>
              </div>
            </div>

            <div class="rrr-stat-card">
              <div class="rrr-stat-icon rrr-stat-icon--blue">
                <i class="fa fa-bell-o" aria-hidden="true"></i>
              </div>
              <div>
                <div class="rrr-stat-num">{{ statValue(initialCount) }}</div>
                <div class="rrr-stat-label">Initial Reminders</div>
              </div>
            </div>

            <div class="rrr-stat-card">
              <div class="rrr-stat-icon rrr-stat-icon--amber">
                <i class="fa fa-repeat" aria-hidden="true"></i>
              </div>
              <div>
                <div class="rrr-stat-num">{{ statValue(followUpCount) }}</div>
                <div class="rrr-stat-label">Follow-up Reminders</div>
              </div>
            </div>

            <div class="rrr-stat-card">
              <div class="rrr-stat-icon rrr-stat-icon--red">
                <i class="fa fa-ban" aria-hidden="true"></i>
              </div>
              <div>
                <div class="rrr-stat-num">{{ statValue(blockedCount) }}</div>
                <div class="rrr-stat-label">
                  Blocked / Failed
                  <span id="rrr-blocked-help" class="rrr-help">
                    <i class="fa fa-question-circle text-warning" aria-hidden="true"></i>
                  </span>
                </div>
                <b-popover target="rrr-blocked-help" triggers="hover" placement="top">
                  <template v-slot:title>What counts as Blocked / Failed</template>
                  <div class="rrr-help-popover">
                    <p>
                      Messages that were prepared and queued but that the carrier or provider
                      would not deliver &mdash; over the carrier's message-length cap, not enough
                      messaging credit, an undeliverable number, or a send error.
                    </p>
                    <p class="mb-0">
                      <span class="fw-semi-bold">Not included:</span> patients who were skipped
                      before anything was sent, such as a patient who has opted out of texts or
                      who has no mobile number on file. Nothing is queued for those patients, so
                      there is no message to report.
                    </p>
                  </div>
                </b-popover>
              </div>
            </div>
          </div>

          <!-- Table -->
          <div class="rrr-table-panel">
            <div class="rrr-table-head">
              <h5 class="rrr-table-title">
                <i class="fa fa-list mr-1" aria-hidden="true"></i>
                Messages Sent
                <span v-if="rows.length" class="badge badge-primary ml-2">{{ rows.length }}</span>
              </h5>
            </div>

            <div v-if="rows.length" class="table-responsive">
              <v-client-table :data="rows" :columns="tableColumns" :options="tableOptions">
                <div slot="DateSent" slot-scope="props">{{ formatSentDate(props.row.DateSent) }}</div>

                <div slot="Patient" slot-scope="props">
                  <a
                    v-if="props.row.Patient"
                    href="#"
                    @click.prevent="gotoPatientPage(props.row.PatNum)"
                  >{{ props.row.Patient }}</a>
                  <span v-else class="text-muted">Patient #{{ props.row.PatNum }}</span>
                </div>

                <div slot="Channel" slot-scope="props">
                  {{ props.row.Channel === 'EMAIL' ? 'Email' : 'Text' }}
                </div>
              </v-client-table>
            </div>

            <div v-else-if="!loading" class="rrr-empty">
              <i class="fa fa-inbox" aria-hidden="true"></i>
              <h4>No recall reminders in this date range</h4>
              <p class="mb-0">
                Nothing was sent between {{ displayDate(dateFrom) }} and
                {{ displayDate(dateTo) }}. Try widening the dates above.
              </p>
            </div>
          </div>
        </b-overlay>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */

/**
 * Recall Reminder Text Report — slide-out report opened from the Overdue Recall page.
 *
 * Replaces the one-off PDF we used to hand-build for practices (first: Smile Bar,
 * 2026-07-29) so they can pull their own recall-reminder send history for any range.
 * Data comes from the RECALL_REMINDER_REPORT case in main-service (SQL in
 * main-service/sql/recall-reminder-report.js), which returns two result sets:
 *   [0] one row per reminder queued   [1] [{ BlockedCount }]
 *
 * Built as a self-contained fixed-position drawer (same shape as
 * InsuranceVerification/components/VerificationDrawer.vue) rather than via
 * $showPanel/vue2-slideout-panel on purpose: that plugin uses ONE shared event bus
 * that every mounted <slideout-panel> listens to, so adding a mount to
 * OverdueRecall.vue would open the panel twice, and the only mount already in that
 * page's tree belongs to AdminTable.vue and is v-if'd on the main list having data.
 */
import DatePicker from 'vue2-datepicker';
import moment from 'moment-timezone';
import { mapActions } from 'vuex';
import mixin from '../../../mixin.js';

const DEFAULT_RANGE_DAYS = 90;

// Report CONTENT uses the same unambiguous style as the PDF this replaces
// ("May 24, 2026") — practices recognise it as the same report, and it can't be
// misread as D/M vs M/D by a non-US office. The date INPUTS still follow the
// site's own DateFormat, matching every other filter in the app.
const DISPLAY_DATE_FORMAT = 'MMM D, YYYY';

export default {
  name: 'RecallReminderReportDrawer',
  components: { DatePicker },
  mixins: [mixin],

  data() {
    return {
      dateFrom: null,
      dateTo: null,
      channel: 'TEXT',
      channelOptions: [
        { value: 'TEXT', text: 'Text messages' },
        { value: 'EMAIL', text: 'Emails' },
        { value: 'ALL', text: 'Text messages and emails' },
      ],
      rows: [],
      blockedCount: 0,
      loading: false,
      hasLoaded: false,
      errorMsg: '',
    };
  },

  created() {
    // The parent v-if's this component, so created() == opened: seed the default
    // window and fetch straight away.
    this.setRange(DEFAULT_RANGE_DAYS);
  },

  mounted() {
    document.addEventListener('keydown', this.onKeydown);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  },

  computed: {
    totalSent() {
      return this.rows.length;
    },

    // AttemptsSoFar === 1 is the initial reminder; anything above it is a follow-up.
    // Counted off the number rather than the Type string so rewording the label can
    // never silently break the tiles.
    initialCount() {
      return this.rows.filter(r => Number(r.AttemptsSoFar) === 1).length;
    },

    followUpCount() {
      return this.rows.filter(r => Number(r.AttemptsSoFar) > 1).length;
    },

    totalLabel() {
      if (this.channel === 'TEXT') return 'Total Texts Sent';
      if (this.channel === 'EMAIL') return 'Total Emails Sent';
      return 'Total Messages Sent';
    },

    subtitle() {
      const what = this.channel === 'TEXT' ? 'texts'
        : this.channel === 'EMAIL' ? 'emails'
        : 'messages';
      return `Automated recall reminder ${what} · ${this.displayDate(this.dateFrom)} – ${this.displayDate(this.dateTo)}`;
    },

    // A Channel column of nothing but "Text" is noise, so only show it when the
    // user has actually asked for more than one channel.
    tableColumns() {
      return this.channel === 'ALL'
        ? ['DateSent', 'Patient', 'Channel', 'Type', 'SentTo']
        : ['DateSent', 'Patient', 'Type', 'SentTo'];
    },

    tableOptions() {
      return {
        headings: {
          DateSent: 'Date Sent',
          Patient: 'Patient',
          Channel: 'Channel',
          Type: 'Type',
          SentTo: 'Text Sent To',
        },
        skin: 'table',
        sortByColumn: true,
        // DateSent sorts on the raw 'YYYY-MM-DD' string the backend sends, which is
        // chronological lexicographically — that's why it isn't pre-formatted.
        sortable: ['DateSent', 'Patient', 'Channel', 'Type', 'SentTo'],
        orderBy: { column: 'DateSent', ascending: false },
        perPage: 50,
        pagination: { chunk: 10, dropdown: false },
        filterByColumn: false,
        filterable: ['Patient', 'Type', 'SentTo'],
        texts: { filter: 'Filter by patient, type or recipient' },
        sortIcon: { base: 'fa', is: 'fa-sort', up: 'fa-sort-asc', down: 'fa-sort-desc' },
      };
    },

    // Which quick-range button (if any) matches the dates currently in the pickers.
    activeQuickRange() {
      for (const days of [30, 90]) {
        const { from, to } = this.rangeFor(days);
        if (this.isoOf(this.dateFrom) === from && this.isoOf(this.dateTo) === to) return days;
      }
      return null;
    },
  },

  methods: {
    ...mapActions('teamio', ['runReportQuery']),

    async load() {
      if (!this.siteID) return;

      this.loading = true;
      this.errorMsg = '';

      try {
        const data = await this.runReportQuery({
          type: 'RECALL_REMINDER_REPORT',
          siteID: this.siteID,
          dateFrom: this.isoOf(this.dateFrom),
          dateTo: this.isoOf(this.dateTo),
          channel: this.channel,
        });

        this.rows = Array.isArray(data) && Array.isArray(data[0]) ? data[0] : [];
        // Result set 1 is the blocked/failed count. It's a separate COUNT rather than
        // a per-row flag because the send ledger has no key back to an individual
        // reminder row — see the SQL module for the full explanation.
        this.blockedCount = Array.isArray(data) && data[1] && data[1][0]
          ? Number(data[1][0].BlockedCount) || 0
          : 0;
        this.hasLoaded = true;
      } catch (err) {
        this.rows = [];
        this.blockedCount = 0;
        this.errorMsg = 'Could not load the recall reminder report. Please try again in a moment.';
      } finally {
        this.loading = false;
      }
    },

    onChannelChange() {
      // Switching channel changes what every tile means, so don't leave stale
      // numbers on screen waiting for an Apply click.
      this.$nextTick(this.load);
    },

    rangeFor(days) {
      return {
        // Inclusive of today, so "last 30 days" spans 30 calendar days, not 31.
        from: moment().subtract(days - 1, 'days').format('YYYY-MM-DD'),
        to: moment().format('YYYY-MM-DD'),
      };
    },

    setRange(days) {
      const { from, to } = this.rangeFor(days);
      this.dateFrom = moment(from, 'YYYY-MM-DD').toDate();
      this.dateTo = moment(to, 'YYYY-MM-DD').toDate();
      this.load();
    },

    isoOf(d) {
      return d ? moment(d).format('YYYY-MM-DD') : '';
    },

    displayDate(d) {
      return d ? moment(d).format(DISPLAY_DATE_FORMAT) : '—';
    },

    // DateSent arrives as a plain 'YYYY-MM-DD' string (the SQL uses date_format for
    // exactly this reason), so parse it with an explicit format. Never run it through
    // a timezone conversion — it is a bare calendar date and converting shifts the day.
    formatSentDate(value) {
      if (!value) return '';
      return moment(String(value).slice(0, 10), 'YYYY-MM-DD').format(DISPLAY_DATE_FORMAT);
    },

    statValue(n) {
      return this.loading && !this.hasLoaded ? '—' : n;
    },

    gotoPatientPage(patNum) {
      const teamioPatientID = `${this.siteID}-${patNum}`;
      const routeData = this.$router.resolve({ name: 'patientprofile', params: { patientID: teamioPatientID } });
      window.open(routeData.href, '_blank');
    },

    // Only bound while the drawer is mounted, i.e. only while it's actually open.
    onKeydown(e) {
      if (e.key === 'Escape') this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
@keyframes rrrScrim {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes rrrSlide {
  from { transform: translateX(24px); opacity: 0.4; }
  to { transform: translateX(0); opacity: 1; }
}

.rrr-scrim {
  position: fixed;
  inset: 0;
  z-index: 1050;
  background: rgba(12, 15, 14, 0.42);
  animation: rrrScrim 0.2s ease;
}

.rrr-drawer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  /* ~70% of the viewport, as asked. The cap keeps a five-column table from
     stretching unreadably wide on an ultra-wide monitor; the floor keeps it
     usable on a laptop. */
  width: clamp(320px, 70vw, 1500px);
  background: #f7f9f9;
  box-shadow: -12px 0 40px rgba(12, 15, 14, 0.18);
  display: flex;
  flex-direction: column;
  animation: rrrSlide 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  color: #0c0f0e;
}

// ── Header ───────────────────────────────────────────────
.rrr-head {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 26px;
  background: #ffffff;
  border-bottom: 1px solid #dfe4e3;
  flex: none;
}

.rrr-headmeta {
  flex: 1;
  min-width: 0;
}

.rrr-title {
  font-size: 21px;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin: 0;
  color: #0c0f0e;
}

.rrr-sub {
  font-size: 13px;
  color: #717977;
  margin-top: 5px;
}

.rrr-close {
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  border: 1px solid #dfe4e3;
  background: #ffffff;
  color: #717977;
  font-size: 16px;
  line-height: 1;

  &:hover {
    background: #f1f4f3;
    color: #0c0f0e;
  }
}

// ── Body ─────────────────────────────────────────────────
.rrr-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 26px 30px;
}

// ── Filters ──────────────────────────────────────────────
.rrr-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 14px;
  background: #ffffff;
  border: 1px solid #dfe4e3;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(12, 15, 14, 0.05);
  padding: 16px 18px;
  margin-bottom: 16px;
}

.rrr-filter {
  flex: 1 1 190px;
  min-width: 170px;

  &--actions {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.rrr-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #717977;
  margin-bottom: 6px;
}

.rrr-quick {
  white-space: nowrap;
}

// ── Summary tiles ────────────────────────────────────────
.rrr-stat-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.rrr-stat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #ffffff;
  border: 1px solid #dfe4e3;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(12, 15, 14, 0.05);
  padding: 16px 18px;
}

.rrr-stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  font-size: 20px;

  &--teal { background: #e9f6f1; color: #0f7259; }
  &--blue { background: #eaf1fb; color: #2a63b0; }
  &--amber { background: #fef6e7; color: #f59e0b; }
  &--red { background: #fdeeee; color: #e5484d; }
}

.rrr-stat-num {
  font-weight: 800;
  font-size: 30px;
  line-height: 1;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: #0c0f0e;
}

.rrr-stat-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #717977;
  margin-top: 6px;
}

.rrr-help {
  cursor: help;
  margin-left: 3px;
}

.rrr-help-popover {
  max-width: 320px;
  font-size: 13px;

  p { margin-bottom: 8px; }
}

// ── Table ────────────────────────────────────────────────
.rrr-table-panel {
  background: #ffffff;
  border: 1px solid #dfe4e3;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(12, 15, 14, 0.05);
  overflow: hidden;
}

.rrr-table-head {
  padding: 14px 18px;
  border-bottom: 1px solid #dfe4e3;
}

.rrr-table-title {
  font-size: 15px;
  font-weight: 700;
  margin: 0;
  color: #0c0f0e;
}

.table-responsive {
  padding: 6px 12px 12px;
}

.rrr-empty {
  text-align: center;
  padding: 46px 20px;
  color: #717977;

  i {
    font-size: 38px;
    color: #c6cfcc;
    margin-bottom: 12px;
    display: block;
  }

  h4 {
    font-size: 16px;
    font-weight: 700;
    color: #0c0f0e;
    margin-bottom: 6px;
  }

  p { font-size: 13px; }
}
</style>
